import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'components/ui/checkbox';
import { Badge } from 'components/ui/badge';
import { formatDuration, metaBits, writerLine } from '../derive-ui';
import type { PublishingTrack, PublishingTrackState } from 'types/publishing';
import { ui } from '../ui';

type Pill = PublishingTrackState | 'hidden' | 'sent';

const pillClass: Record<Pill, string> = {
  listed: 'border-transparent bg-green-100 text-green-800 hover:bg-green-100',
  not_listed: 'border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100',
  cant_check: 'border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100',
  hidden: 'border-transparent bg-gray-100 text-gray-700 hover:bg-gray-100',
  sent: 'border-transparent bg-blue-100 text-blue-800 hover:bg-blue-100',
};

interface Props {
  track: PublishingTrack;
  legalName: string | null;
  check?: boolean;
  checked?: boolean;
  notMine?: boolean;
  pill?: Pill;
  readOnly: boolean;
  onOpen: (t: PublishingTrack) => void;
  onCheck?: (isrc: string, on: boolean) => void;
  onNotMine?: (isrc: string) => void;
}

export function SongRow({
  track,
  legalName,
  check,
  checked,
  notMine,
  pill,
  readOnly,
  onOpen,
  onCheck,
  onNotMine,
}: Props) {
  const { t } = useTranslation('publishing');
  const wline = writerLine(t, legalName, track.writers);
  const duration = formatDuration(track.durationSeconds);
  const meta = metaBits(track);
  return (
    <div
      className="cursor-pointer border-b border-[#E9E9E9] py-3.5 last:border-b-0 hover:bg-gray-50"
      onClick={() => onOpen(track)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(track);
      }}
    >
      <div className="flex items-start gap-3">
        {check && (
          <div className="pt-1" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={!!checked}
              disabled={readOnly}
              onCheckedChange={(v) => onCheck && track.isrc && onCheck(track.isrc, !!v)}
              aria-label={t('include', { title: track.title })}
              className="data-[state=checked]:bg-[#7801ef] border-gray-400 data-[state=checked]:border-[#7801ef]"
            />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className={ui.subhead}>{track.title}</div>
          {meta && <div className={`mt-1 ${ui.muted}`}>{meta}</div>}
          {wline && <div className={`mt-1 ${ui.body}`}>{wline}</div>}
        </div>
        {duration && (
          <span className={`min-w-[36px] text-right tabular-nums ${ui.muted}`}>{duration}</span>
        )}
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
          {pill && (
            <Badge className={`text-sm font-medium ${pillClass[pill]}`}>{t(`pill_${pill}`)}</Badge>
          )}
          {notMine && (
            <button
              type="button"
              className={`${ui.link} disabled:cursor-not-allowed disabled:opacity-50`}
              disabled={readOnly}
              onClick={(e) => {
                e.stopPropagation();
                if (onNotMine && track.isrc) onNotMine(track.isrc);
              }}
            >
              {t('not_mine')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
